/**
 * demoStore — BigInt-safe localStorage persistence for mock/demo mode.
 *
 * Why: standard JSON.stringify/parse can't handle BigInt, but BizOP uses
 * BigInt cents everywhere. We encode them as tagged strings ("BI:12345")
 * and revive them on read.
 *
 * API
 * ───
 *   demoStore.get<T>(key, fallback)  — read (deserialise) or return fallback
 *   demoStore.set<T>(key, value)     — write (serialise)
 *   demoStore.clear()                — wipe all "demo:" keys → reset to seed
 *   demoStore.has(key)               — check if a key is persisted
 *
 * All keys are namespaced under "demo:" automatically.
 *
 * Usage:
 *   import { demoStore } from "./demoStore";
 *   const dealers = demoStore.get("dealers", mockDealers);
 *   demoStore.set("dealers", dealers);
 */

const NS = "demo:";

/* ─── BigInt serialisation ─────────────────────────────────────────────── */

function replacer(_key: string, value: unknown): unknown {
  if (typeof value === "bigint") {
    return `BI:${value.toString()}`;
  }
  return value;
}

function reviver(_key: string, value: unknown): unknown {
  if (typeof value === "string" && value.startsWith("BI:")) {
    return BigInt(value.slice(3));
  }
  return value;
}

function serialize<T>(value: T): string {
  return JSON.stringify(value, replacer);
}

function deserialize<T>(raw: string): T {
  return JSON.parse(raw, reviver) as T;
}

/* ─── Store ─────────────────────────────────────────────────────────────── */

function key(name: string): string {
  return `${NS}${name}`;
}

export const demoStore = {
  /** Read a value from localStorage, returning `fallback` when absent/corrupt. */
  get<T>(name: string, fallback: T): T {
    try {
      const raw = localStorage.getItem(key(name));
      if (raw == null) return fallback;
      return deserialize<T>(raw);
    } catch {
      return fallback;
    }
  },

  /** Write a value to localStorage. Silently swallows quota / security errors. */
  set<T>(name: string, value: T): void {
    try {
      localStorage.setItem(key(name), serialize(value));
    } catch {
      /* quota exceeded or private-browsing restriction — silently ignore */
    }
  },

  /** Returns true if this key has a persisted value (even if it's the seed). */
  has(name: string): boolean {
    try {
      return localStorage.getItem(key(name)) !== null;
    } catch {
      return false;
    }
  },

  /**
   * Remove all "demo:" keys from localStorage.
   * After calling this, the next page load reverts to seed mock data.
   */
  clear(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith(NS)) keysToRemove.push(k);
      }
      for (const k of keysToRemove) {
        localStorage.removeItem(k);
      }
    } catch {
      /* ignore */
    }
  },
};
